import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { debounceTime, lastValueFrom, map, Subject } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { User } from '../../../api/app-types/user.app';
import { USER_SERVICE, UserApi } from '../../../api/services/user.service';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { Paginator } from '../../../core/components/paginator/paginator';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { UserDeactivateDialog } from './user-deactivate-dialog';
import { UserDeleteDialog } from './user-delete-dialog';
import { UserListQueryParams, UserSortField } from './user-list-request';
import { UserReactivateDialog } from './user-reactivate-dialog';

type UserSearchField = 'firstName' | 'lastName' | 'email';
type SortDirection = 'asc' | 'desc';
interface UserSearchQuery {
	firstName?: string;
	name?: string;
	email?: string;
}

@Component({
	selector: 'lvr-user-list',
	templateUrl: './user-list.html',
	styleUrl: './user-list.scss',
	imports: [
		MatIconButton,
		MatIcon,
		MatMenuModule,
		MatProgressBarModule,
		MatTableModule,
		MatTooltipModule,
		Paginator,
		TranslocoPipe,
		LvrHeading,
		LvrButton,
	],
	providers: [{ provide: USER_SERVICE, useClass: UserApi }],
})
export class UserList extends RouteInjection {
	private readonly routeData = toSignal(inject(ActivatedRoute).data);
	private readonly userService = inject(USER_SERVICE);
	private readonly env = inject(ENVIRONMENT);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly dialog = inject(MatDialog);
	private readonly session = inject(SessionManagerService);
	public readonly canCreateUsers = hasPermission(this.session.permissions, 'Users.Create');
	public readonly canEditUsers = hasPermission(this.session.permissions, 'Users.Edit');
	public readonly canDeleteUsers = hasPermission(this.session.permissions, 'Users.Delete');

	public readonly showActiveUsers = signal(true);

	public readonly initialUserPage = computed<PaginatedResponse<User>>(() => {
		const data = this.routeData();
		return data && 'users' in data
			? (data['users'] as PaginatedResponse<User>)
			: { entities: [], offset: 0, range: 0, totalEntities: 0 };
	});
	public readonly userPage = signal<PaginatedResponse<User>>({ entities: [], offset: 0, range: 0, totalEntities: 0 });

	public readonly loading = signal(false);

	public readonly displayedColumns = ['status', 'firstName', 'lastName', 'email', 'actions'];
	public readonly pageIndex = signal(0);
	public readonly pageSize = signal(this.env.defaultPageSize);

	public readonly users = computed(() => this.userPage().entities);
	public readonly displayedUsers = this.users;
	public readonly totalItems = computed(() => this.userPage().totalEntities);
	public readonly hasActiveSearch = computed(() => {
		const filters = this.appliedSearchFilters();
		return Boolean(filters.firstName || filters.name || filters.email);
	});
	public readonly paginatorLength = this.totalItems;

	public readonly sortField = signal<UserSortField>('lastName');
	public readonly sortDirection = signal<SortDirection>('asc');
	public readonly activeSortField = signal<UserSortField>('lastName');
	public readonly filterSearch = signal<Record<UserSortField, string>>({
		firstName: '',
		lastName: '',
		email: '',
	});

	private readonly destroyRef = inject(DestroyRef);
	private readonly searchInput$ = new Subject<Record<UserSearchField, string>>();
	private readonly appliedSearchFilters = signal<UserSearchQuery>({});
	private latestRequestId = 0;

	public constructor() {
		super();

		const initialPage = this.initialUserPage();

		this.userPage.set(initialPage);

		this.pageSize.set(initialPage.range || this.env.defaultPageSize);

		this.pageIndex.set(initialPage.offset || 0);

		this.searchInput$
			.pipe(
				debounceTime(300),
				map((filters) => this.normalizeSearchFilters(filters)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((filters) => {
				const currentFilters = this.appliedSearchFilters();

				if (this.areSearchFiltersEqual(currentFilters, filters)) {
					return;
				}

				this.appliedSearchFilters.set(filters);

				this.pageIndex.set(0);

				void this.reloadCurrentPage();
			});
	}

	private normalizeSearchFilters(filters: Record<UserSearchField, string>): UserSearchQuery {
		const firstName = filters.firstName.trim();
		const lastName = filters.lastName.trim();
		const email = filters.email.trim();

		return {
			...(firstName.length >= 3 ? { firstName } : {}),

			...(lastName.length >= 3 ? { name: lastName } : {}),

			...(email.length >= 3 ? { email } : {}),
		};
	}

	private areSearchFiltersEqual(first: UserSearchQuery, second: UserSearchQuery): boolean {
		return first.firstName === second.firstName && first.name === second.name && first.email === second.email;
	}

	public openCreate() {
		if (!this.canCreateUsers()) return;
		this.router.navigate(['/admin/users/add']);
	}

	public setStatusFilter(showActive: boolean) {
		if (showActive === this.showActiveUsers()) return;
		this.showActiveUsers.set(showActive);
		this.pageIndex.set(0);
		void this.reloadCurrentPage();
	}

	public setSort(field: UserSortField, direction: SortDirection): void {
		if (this.sortField() === field && this.sortDirection() === direction) {
			return;
		}

		this.sortField.set(field);
		this.sortDirection.set(direction);
		this.pageIndex.set(0);

		void this.reloadCurrentPage();
	}

	public resetAll(): void {
		const hasChanges =
			this.showActiveUsers() !== true ||
			this.sortField() !== 'lastName' ||
			this.sortDirection() !== 'asc' ||
			this.pageIndex() !== 0 ||
			Object.values(this.filterSearch()).some((value) => value.trim().length > 0);

		if (!hasChanges) {
			return;
		}

		this.showActiveUsers.set(true);
		this.sortField.set('lastName');
		this.sortDirection.set('asc');
		this.activeSortField.set('lastName');
		this.filterSearch.set({
			firstName: '',
			lastName: '',
			email: '',
		});
		this.appliedSearchFilters.set({});
		this.pageIndex.set(0);
		void this.reloadCurrentPage();
	}

	public setActiveSortField(field: UserSortField) {
		this.activeSortField.set(field);
	}

	public isSortOptionActive(field: UserSortField, direction: SortDirection): boolean {
		return this.sortField() === field && this.sortDirection() === direction;
	}

	public sortLabel(direction: SortDirection): string {
		return direction === 'asc'
			? 'database_view.assignments.sort.alphabetical_ascending'
			: 'database_view.assignments.sort.alphabetical_descending';
	}

	public setFilterSearch(key: UserSearchField, value: string): void {
		const filters = {
			...this.filterSearch(),
			[key]: value,
		};

		this.filterSearch.set(filters);
		this.searchInput$.next(filters);
	}

	public openEdit(user: User, event: Event) {
		event.stopPropagation();
		if (!this.canEditUsers()) return;
		this.router.navigate([user.objectId, 'edit'], { relativeTo: this.route });
	}

	public openDetails(user: User) {
		this.router.navigate([user.objectId], { relativeTo: this.route });
	}

	public async handlePageEvent(event: PageEvent): Promise<void> {
		const pageSizeChanged = event.pageSize !== this.pageSize();

		this.pageSize.set(event.pageSize);

		this.pageIndex.set(pageSizeChanged ? 0 : event.pageIndex);

		await this.reloadCurrentPage();
	}

	public async triggerDeactivate(user: User, event: Event) {
		event.stopPropagation();
		if (!this.canEditUsers()) return;
		const dialogRef = this.dialog.open(UserDeactivateDialog, {
			data: user,
			width: '500px',
			disableClose: true,
		});

		const result = await lastValueFrom(dialogRef.afterClosed());

		if (!result) return;
		// User was deactivated, reload the current page
		this.reloadCurrentPage();
	}

	public async triggerDelete(user: User, event: Event) {
		event.stopPropagation();
		if (!this.canDeleteUsers()) return;
		const dialogRef = this.dialog.open(UserDeleteDialog, {
			data: user,
			width: '500px',
			disableClose: true,
		});

		const result = await lastValueFrom(dialogRef.afterClosed());

		if (!result) return;
		// User was deactivated, reload the current page
		this.reloadCurrentPage();
	}

	public async triggerReactivate(user: User, event: Event) {
		event.stopPropagation();
		if (!this.canEditUsers()) return;
		const dialogRef = this.dialog.open(UserReactivateDialog, {
			data: user,
			width: '500px',
			disableClose: true,
		});

		const result = await lastValueFrom(dialogRef.afterClosed());

		if (!result) return;

		this.reloadCurrentPage();
	}

	private async reloadCurrentPage(): Promise<void> {
		const requestId = ++this.latestRequestId;

		this.loading.set(true);

		const queryParams: UserListQueryParams = {
			offset: this.pageIndex(),
			range: this.pageSize(),
			activeUsers: this.showActiveUsers(),
			sortBy: this.sortField(),
			ascending: this.sortDirection() === 'asc',
			...this.appliedSearchFilters(),
		};

		try {
			const response = await this.userService.getAll(queryParams);

			if (requestId !== this.latestRequestId) {
				return;
			}

			this.userPage.set(response);
			this.pageIndex.set(response.offset);
		} catch {
			if (requestId !== this.latestRequestId) {
				return;
			}

			this.ntfySrv.addNotification({
				id: window.crypto.randomUUID(),
				message: 'Fehler beim Neuladen der Anwenderliste',
				showToast: true,
				timestamp: new Date(Date.now()),
				type: 'error',
			});
		} finally {
			if (requestId === this.latestRequestId) {
				this.loading.set(false);
			}
		}
	}
}
